import Splide from '@splidejs/splide';

/**
 * @typedef {Object} DuoShopState
 * @property {boolean} mediaLoaded - Whether the media (image or video) has finished loading
 * @property {boolean} inViewport - Whether the section is currently within the viewport
 */

/**
 * Custom web component for the Duo Shop module.
 * Waits for both media readiness (image/video) and viewport intersection
 * before transitioning the content overlay into view.
 * Initializes a Splide slider for the content cards panel.
 * @extends HTMLElement
 */
class DuoShopModule extends HTMLElement {
  constructor() {
    super();

    /** @type {DuoShopState} */
    this.state = {
      mediaLoaded: false,
      inViewport: false,
    };

    /** @type {AbortController|null} */
    this.abortController = null;

    /** @type {IntersectionObserver|null} */
    this.observer = null;

    /** @type {Splide|null} */
    this.slider = null;
  }

  /**
   * Lifecycle callback — element inserted into the DOM.
   * Sets up media load listeners and an IntersectionObserver to gate
   * the content transition on both conditions being met.
   * Initializes the content cards Splide slider.
   * @returns {void}
   */
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.observeViewport();
    this.initSlider();

    const mediaContainer = this.querySelector('.js-duo-shop-media');
    if (!mediaContainer) return;

    const video = mediaContainer.querySelector('video');
    const image = mediaContainer.querySelector('img');

    if (video) {
      this.waitForVideo(video, signal);
    } else if (image) {
      this.waitForImage(image, signal);
    }
  }

  /**
   * Lifecycle callback — element removed from the DOM.
   * Cleans up all event listeners, the IntersectionObserver, and the Splide instance.
   * @returns {void}
   */
  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;

    this.observer?.disconnect();
    this.observer = null;

    this.slider?.destroy(true);
    this.slider = null;
  }

  /**
   * Creates an IntersectionObserver on the host element.
   * Sets `inViewport` to true the first time the element enters
   * the viewport, then disconnects.
   * @returns {void}
   */
  observeViewport() {
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          this.state.inViewport = true;
          this.observer.disconnect();
          this.observer = null;
          this.revealContent();
        }
      },
      { threshold: 0.2 }
    );

    this.observer.observe(this);
  }

  /**
   * Attaches a load listener to an image element.
   * If the image is already cached / complete, marks loaded immediately.
   * @param {HTMLImageElement} img - The image element to observe
   * @param {AbortSignal} signal - Signal for listener cleanup
   * @returns {void}
   */
  waitForImage(img, signal) {
    if (img.complete && img.naturalWidth > 0) {
      this.state.mediaLoaded = true;
      this.revealContent();
      return;
    }

    img.addEventListener(
      'load',
      () => {
        this.state.mediaLoaded = true;
        this.revealContent();
      },
      { once: true, signal }
    );
  }

  /**
   * Attaches a load listener to a video element.
   * Uses the `canplaythrough` event so the transition doesn't fire before
   * enough data has buffered for smooth playback.
   * @param {HTMLVideoElement} video - The video element to observe
   * @param {AbortSignal} signal - Signal for listener cleanup
   * @returns {void}
   */
  waitForVideo(video, signal) {
    if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      this.state.mediaLoaded = true;
      this.revealContent();
      return;
    }

    video.addEventListener(
      'canplaythrough',
      () => {
        this.state.mediaLoaded = true;
        this.revealContent();
      },
      { once: true, signal }
    );
  }

  /**
   * Initializes the Splide slider for the content cards panel.
   * Active on mobile/tablet only — `destroy: true` at the desktop
   * breakpoint tears down Splide so CSS grid takes over.
   * @returns {void}
   */
  initSlider() {
    const sliderEl = this.querySelector('.js-duo-shop-slider');
    if (!sliderEl || this.slider) return;

    this.slider = new Splide(sliderEl, {
      type: 'slide',
      arrows: false,
      pagination: true,
      mediaQuery: 'min',
      start: 0,
      omitEnd: true,
      fixedWidth: '38%',
      gap: '1rem',
      trimSpace: true,
      drag: true,
      snap: true,
      breakpoints: {
        1025: {
          fixedWidth: '100%',
          destroy: true,
        },
      },
    });

    this.slider.mount();
  }

  /**
   * Reveals the content overlay only when both conditions are met:
   * the media has loaded AND the section is in the viewport.
   * Adds `is-visible` to the content element and sets a `loaded`
   * attribute on the host for CSS hooks.
   * @returns {void}
   */
  revealContent() {
    if (!this.state.mediaLoaded || !this.state.inViewport) return;

    const content = this.querySelector('.module-duo-shop__content');
    if (content) {
      content.classList.add('is-visible');
    }

    this.setAttribute('loaded', '');
  }
}

customElements.define('duo-shop-module', DuoShopModule);
